import {
  Box,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PropTypes from 'prop-types';
import { getOptionValue } from '@lib/form';
import { StandaloneFieldLabel } from '../../FieldLabel';
import { FIELD_BORDER_COLOR, FIELD_HEIGHT } from '../../formTheme';
import getFieldStatus from '../../fieldStatus';

/**
 * A row of buttons instead of a drop-down.
 *
 * For a three-option question like Anrede, a `<select>` costs a tap to open, a scan of a
 * floating list and a tap to choose, and hides the options until then. Laid out as segments
 * the whole question is one glance and one tap — and it needs no new field type in the CMS,
 * only `variant: "segmented"` on the existing select.
 *
 * Best kept to option sets that fit one row; longer lists want `variant: "searchable"`.
 */
export default function Segmented(props) {
  const {
    name,
    disabled,
    value,
    label,
    isRequired,
    error,
    touched,
    helperText = '',
    isMultiple,
    options = [],
    handleChange,
    setFieldTouched,
  } = props;
  const { hasError, message } = getFieldStatus({ error, touched, helperText });

  const handleToggle = (event, newValue) => {
    setFieldTouched(name, true, false);

    if (isMultiple) {
      handleChange(newValue ?? []);
      return;
    }

    // `exclusive` hands back null when the selected button is pressed again. Clearing is
    // right for an optional question; for a required one it would only ever re-open the
    // error the user just answered.
    if (newValue === null && isRequired) {
      return;
    }

    handleChange(newValue ?? '');
  };

  const helperId = `${name}-helper`;
  const showHelper = Boolean(message);

  return (
    // Positioned so the label can sit *in* the button row's top border rather than stacked
    // above it. Stacked, the label's line box plus its margin pushed the buttons ~28px below
    // the outlined inputs sharing the row, because their label is notched into the border and
    // costs no height. This keeps the control tops and the label baselines aligned.
    <Box sx={{ position: 'relative' }}>
      <StandaloneFieldLabel
        id={`${name}-label`}
        isRequired={isRequired}
        label={label}
        sx={{
          position: 'absolute',
          // 14px is MUI's own inset for a shrunk outlined label; -50% centres the text on
          // the border the way the notch does.
          top: 0,
          left: 14,
          transform: 'translateY(-50%)',
          mb: 0,
          px: 0.5,
          // Interrupts the button's border, which is what makes it read as a notch. Half the
          // theme radius: the full 12px the buttons take would round this ~16px-tall box into
          // a pill, which reads as a badge rather than a cut-out.
          backgroundColor: 'background.paper',
          borderRadius: 0.5,
          // Load-bearing: `ToggleButton` extends `ButtonBase`, which is `position: relative`
          // for its ripple. So the buttons are positioned elements too, with `z-index: auto`
          // and later in document order — which is exactly the tie-break that had the first
          // one painting over the label.
          zIndex: 1,
          // The label is decorative here — `aria-labelledby` already names the group.
          pointerEvents: 'none',
          maxWidth: 'calc(100% - 28px)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      />
      {/*
        `ToggleButtonGroup` rather than a hand-rolled row of buttons with `role="radio"`:
        the ARIA radiogroup pattern also requires roving tabindex and arrow-key navigation,
        which the hand-rolled version did not implement — every option was tabbable and the
        arrows did nothing. MUI ships the group semantics, the selected state and the
        keyboard handling.
      */}
      <ToggleButtonGroup
        aria-describedby={showHelper ? helperId : undefined}
        aria-labelledby={label ? `${name}-label` : undefined}
        disabled={disabled}
        exclusive={!isMultiple}
        onChange={handleToggle}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          // Undo the joined-segment look: the design shows separate rounded buttons, and
          // `ToggleButtonGroup` collapses the inner borders and corners by default.
          '& .MuiToggleButtonGroup-grouped': {
            flex: 1,
            minWidth: 'fit-content',
            height: FIELD_HEIGHT,
            border: '1px solid',
            borderColor: FIELD_BORDER_COLOR,
            borderRadius: 1,
            ml: 0,
          },
        }}
        value={isMultiple ? (value ?? []) : (value ?? null)}
      >
        {options.map((item) => {
          const itemValue = getOptionValue(item);
          const isSelected = isMultiple
            ? (value ?? []).includes(itemValue)
            : value === itemValue;

          return (
            <ToggleButton
              key={item.id}
              sx={{
                textTransform: 'none',
                fontSize: 15,
                fontWeight: isSelected ? 700 : 600,
                '&.Mui-selected': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'primary.main' },
                },
              }}
              value={itemValue}
            >
              {item.label}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      {showHelper && (
        <FormHelperText error={hasError} id={helperId}>
          {message}
        </FormHelperText>
      )}
    </Box>
  );
}

Segmented.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  error: PropTypes.string,
  // Formik reports an array-valued field's touched state as an array of booleans,
  // not a boolean.
  touched: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  helperText: PropTypes.string,
  isMultiple: PropTypes.bool,
  options: PropTypes.array,
  handleChange: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
};
